import React from 'react';
import { motion } from 'framer-motion';
import { QrCode, Smartphone, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../hooks/useLanguage';
interface StepConnectionMethodProps {
  onSelectQR: () => void;
  onSelectPairingCode: () => void;
  onBack: () => void;
}
export function StepConnectionMethod({
  onSelectQR,
  onSelectPairingCode,
  onBack
}: StepConnectionMethodProps) {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const methods = [{
    id: 'qr',
    icon: QrCode,
    title: isRTL ? 'مسح رمز QR' : 'Scan QR Code',
    description: isRTL ? 'امسح الرمز من هاتفك للربط السريع' : 'Scan the code from your phone for quick connection',
    color: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200 hover:border-purple-400',
    onClick: onSelectQR
  }, {
    id: 'pairing',
    icon: Smartphone,
    title: isRTL ? 'كود الاقتران' : 'Pairing Code',
    description: isRTL ? 'أدخل الكود في واتساب للربط بدون مسح' : 'Enter the code in WhatsApp to connect without scanning',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200 hover:border-emerald-400',
    onClick: onSelectPairingCode,
    badge: isRTL ? 'جديد' : 'New'
  }];
  return <div className="space-y-6" data-id="element-495">
      {/* Header */}
      <div className="text-center space-y-2" data-id="element-496">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200
      }} className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mb-4" data-id="element-497">
          <Sparkles className="w-8 h-8 text-purple-600" data-id="element-498" />
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-2xl font-bold text-gray-900" data-id="element-499">
          {isRTL ? 'اختر طريقة الربط' : 'Choose Connection Method'}
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-gray-500" data-id="element-500">
          {isRTL ? 'اختر الطريقة المناسبة لربط واتساب' : 'Select the best way to connect your WhatsApp'}
        </motion.p>
      </div>

      {/* Connection Methods */}
      <div className="space-y-4 mt-8" data-id="element-501">
        {methods.map((method, index) => <motion.button key={method.id} initial={{
        opacity: 0,
        x: isRTL ? 20 : -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2 + index * 0.1
      }} whileHover={{
        scale: 1.02,
        y: -2
      }} whileTap={{
        scale: 0.98
      }} onClick={method.onClick} className={`w-full p-5 rounded-2xl border-2 ${method.borderColor} ${method.bgColor} transition-all duration-200 text-${isRTL ? 'right' : 'left'} relative overflow-hidden group`} data-id="element-502">
            {/* Badge */}
            {method.badge && <motion.span initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.5,
          type: 'spring'
        }} className="absolute top-3 right-3 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full" data-id="element-503">
                {method.badge}
              </motion.span>}

            <div className="flex items-center gap-4" data-id="element-504">
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`} data-id="element-505">
                <method.icon className="w-7 h-7 text-white" data-id="element-506" />
              </div>

              {/* Content */}
              <div className="flex-1" data-id="element-507">
                <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-508">
                  {method.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-509">{method.description}</p>
              </div>

              {/* Arrow */}
              <motion.div className="text-gray-400 group-hover:text-gray-600" animate={{
            x: [0, 5, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-510">
                {isRTL ? <ArrowLeft className="w-5 h-5" data-id="element-511" /> : <ArrowRight className="w-5 h-5" data-id="element-512" />}
              </motion.div>
            </div>
          </motion.button>)}
      </div>

      {/* Back Button */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.5
    }} className="pt-4" data-id="element-513">
        <Button type="button" variant="secondary" onClick={onBack} fullWidth data-id="element-514">
          {isRTL ? 'رجوع' : 'Back'}
        </Button>
      </motion.div>
    </div>;
}